import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import { getChargeSla, getDndChargeSla } from "../../../services/metricsApi";
import StatTile, { formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import "../Analytics.css";

const PAGE_SIZE = 50;
const fetchOverallChargeSla = async (filters) => {
  const [standard, dnd] = await Promise.all([
    getChargeSla(filters),
    getDndChargeSla(filters),
  ]);
  const standardSummary = standard.sla_summary || {};
  const dndSummary = dnd.sla_summary || {};

  return {
    records: [
      ...(standard.records || []).map((record) => ({ ...record, charge_category: "Standard" })),
      ...(dnd.records || []).map((record) => ({ ...record, charge_category: "D&D" })),
    ],
    total_records: Number(standard.total_records || 0) + Number(dnd.total_records || 0),
    sla_summary: {
      within_sla: Number(standardSummary.within_sla || 0) + Number(dndSummary.within_sla || 0),
      breached: Number(standardSummary.breached || 0) + Number(dndSummary.breached || 0),
      unknown: Number(standardSummary.unknown || 0) + Number(dndSummary.unknown || 0),
    },
    warning: standard.warning || dnd.warning,
  };
};

const SLA_VIEWS = {
  overall: {
    label: "Overall Unplanned Charges",
    fetcher: fetchOverallChargeSla,
    rules: "Standard and Detention & Demurrage charge SLA records",
  },
  dnd: {
    label: "Detention & Demurrage SLA",
    fetcher: getDndChargeSla,
    rules: "Origin: ETD + 14 days · Destination: Delivery Actual + 14 days",
  },
};

const DIRECTIONS = [
  { key: "origin", label: "Origin", queryValue: "Origin" },
  { key: "destination", label: "Destination", queryValue: "Destination" },
];

const COMMON_TABLE_COLUMNS = [
  { id: "charge_id", label: "Charge ID" },
  { id: "charge_name", label: "Charge" },
  { id: "created_date", label: "Created Date" },
  { id: "origin_destination", label: "Origin / Destination" },
  { id: "material_name", label: "Material" },
  { id: "sla_days", label: "SLA Days" },
  { id: "sla_reference_field", label: "Reference Field" },
  { id: "sla_reference_date", label: "Reference Date" },
  { id: "sla_due_date", label: "SLA Due Date" },
  { id: "sla_status", label: "SLA Status" },
];

const TABLE_COLUMNS = {
  overall: [
    { id: "charge_category", label: "Charge Type" },
    ...COMMON_TABLE_COLUMNS.slice(0, 5),
    { id: "etd", label: "ETD" },
    { id: "eta", label: "ETA" },
    { id: "delivery_actual", label: "Delivery Actual" },
    ...COMMON_TABLE_COLUMNS.slice(5),
  ],
  dnd: [
    ...COMMON_TABLE_COLUMNS.slice(0, 5),
    { id: "etd", label: "ETD" },
    { id: "delivery_actual", label: "Delivery Actual" },
    ...COMMON_TABLE_COLUMNS.slice(5),
  ],
};

const IRRELEVANT_DATE_COLUMN = {
  overall: { origin: "", destination: "" },
  dnd: { origin: "delivery_actual", destination: "etd" },
};

const readCount = (summary, key) => Number(summary?.[key] || 0);

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const fetchDirection = async (fetcher, filters, queryValue, page = 1, slaStatus = "all") => {
  const data = await fetcher({
    ...filters,
    page,
    page_size: PAGE_SIZE,
    charge_type: queryValue,
    sla_status: slaStatus === "all" ? undefined : slaStatus,
  });
  return {
    records: data.records || [],
    totalRecords: Number(data.total_records || 0),
    summary: data.sla_summary || {},
    page,
    warning: data.warning,
  };
};

const CXTimelinessSection = ({ id, filters, refreshKey }) => {
  const [activeView, setActiveView] = useState("overall");
  const [scope, setScope] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [tablePage, setTablePage] = useState(1);
  const [result, setResult] = useState({ loading: true, loadingMore: false, error: null, origin: null, destination: null });
  const view = SLA_VIEWS[activeView];

  useEffect(() => {
    let cancelled = false;
    setTablePage(1);
    setResult({ loading: true, loadingMore: false, error: null, origin: null, destination: null });

    Promise.all(
      DIRECTIONS.map(({ key, queryValue }) =>
        fetchDirection(view.fetcher, filters, queryValue, 1, selectedStatus)
      )
    )
      .then(([origin, destination]) => {
        if (!cancelled) setResult({ loading: false, loadingMore: false, error: null, origin, destination });
      })
      .catch((error) => {
        if (!cancelled) setResult({ loading: false, loadingMore: false, error: error.message, origin: null, destination: null });
      });

    return () => {
      cancelled = true;
    };
  }, [view, filters, refreshKey, selectedStatus]);

  const activeDirections = scope === "all" ? DIRECTIONS : DIRECTIONS.filter(({ key }) => key === scope);
  const tableColumns = TABLE_COLUMNS[activeView].filter(({ id: columnId }) =>
    scope === "all" || columnId !== IRRELEVANT_DATE_COLUMN[activeView][scope]
  );
  const summaries = activeDirections.map(({ key }) => result[key]?.summary).filter(Boolean);
  const counts = {
    within: summaries.reduce((total, summary) => total + readCount(summary, "within_sla"), 0),
    breached: summaries.reduce((total, summary) => total + readCount(summary, "breached"), 0),
    unknown: summaries.reduce((total, summary) => total + readCount(summary, "unknown"), 0),
  };
  const knownCount = counts.within + counts.breached;
  const rows = activeDirections.flatMap(({ key }) => result[key]?.records || []);
  const matchingRows = selectedStatus === "all"
    ? rows
    : rows.filter((record) => String(record.sla_status || "unknown").toLowerCase() === selectedStatus);
  const tableData = matchingRows.map((record, index) => ({
    ...record,
    _id: `${record.charge_id || record.charge_name || "charge"}-${index}`,
    charge_category: record.charge_category || "-",
    charge_id: record.charge_id || "-",
    charge_name: record.charge_name || "-",
    material_name: record.material_name || "-",
    origin_destination: record.origin_destination || "-",
    created_date: formatDate(record.created_date),
    sla_reference_field: record.sla_reference_field || "-",
    sla_reference_date: formatDate(record.sla_reference_date),
    sla_due_date: formatDate(record.sla_due_date),
    sla_days: record.sla_days ?? "-",
    sla_status: String(record.sla_status || "unknown").replaceAll("_", " "),
    etd: formatDate(record.etd),
    eta: formatDate(record.eta),
    delivery_actual: formatDate(record.delivery_actual),
  }));
  const visibleTableData = tableData.slice((tablePage - 1) * PAGE_SIZE, tablePage * PAGE_SIZE);

  const breakdown = (key) => DIRECTIONS
    .map(({ key: direction, label }) => `${label}: ${formatCount(readCount(result[direction]?.summary, key))}`)
    .join(" · ");

  const warning = activeDirections.map(({ key }) => result[key]?.warning).find(Boolean);
  const totalRecords = activeDirections.reduce((total, { key }) => total + (result[key]?.totalRecords || 0), 0);
  const selectedTotal = selectedStatus === "within_sla"
    ? counts.within
    : selectedStatus === "breached"
      ? counts.breached
      : selectedStatus === "unknown"
        ? counts.unknown
        : totalRecords;
  const pageCount = Math.max(1, Math.ceil(selectedTotal / PAGE_SIZE));

  const fetchUntilMatches = async (requiredCount, status) => {
    const countLoaded = (currentResult) => activeDirections.reduce(
      (count, { key }) => count + (currentResult[key]?.records.length || 0),
      0
    );
    setResult((current) => ({ ...current, loadingMore: true, error: null }));
    let latestResult = result;
    try {
      while (
        countLoaded(latestResult) < requiredCount &&
        activeDirections.some(({ key }) =>
          latestResult[key] && latestResult[key].records.length < latestResult[key].totalRecords
        )
      ) {
        const updates = await Promise.all(activeDirections.map(async ({ key, queryValue }) => {
          const current = latestResult[key];
          if (!current || current.records.length >= current.totalRecords) return [key, current];
          const next = await fetchDirection(
            view.fetcher,
            filters,
            queryValue,
            current.page + 1,
            status
          );
          return [key, { ...next, records: [...current.records, ...next.records] }];
        }));
        latestResult = { ...latestResult, ...Object.fromEntries(updates) };
      }
      setResult({ ...latestResult, loading: false, loadingMore: false, error: null });
      return true;
    } catch (error) {
      setResult((current) => ({ ...current, loadingMore: false, error: error.message }));
      return false;
    }
  };

  const handlePageChange = async (nextPage) => {
    if (result.loading || result.loadingMore || nextPage < 1 || nextPage > pageCount) return;
    const requiredCount = nextPage * PAGE_SIZE;
    if (tableData.length < requiredCount) {
      const loaded = await fetchUntilMatches(requiredCount, selectedStatus);
      if (!loaded) return;
    }
    setTablePage(nextPage);
  };

  const toggleStatus = (status) => {
    if (result.loading || result.loadingMore) return;
    setSelectedStatus((current) => current === status ? "all" : status);
    setTablePage(1);
  };
  const handleStatusKeyDown = (status) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleStatus(status);
    }
  };

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">CX Timeliness</h2>
        <span className="analytics-section-caption">Charge-level SLA status</span>
      </div>

      <div className="analytics-section-card cx-timeliness-card">
        <div className="tl-tab-strip cx-sla-tabs" role="tablist" aria-label="CX SLA type">
          {Object.entries(SLA_VIEWS).map(([key, item]) => (
            <button
              key={key}
              id={`cx-sla-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={activeView === key}
              aria-controls="cx-sla-panel"
              className={`tl-tab${activeView === key ? " tl-tab-active" : ""}`}
              onClick={() => {
                setSelectedStatus("all");
                setActiveView(key);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div id="cx-sla-panel" role="tabpanel" aria-labelledby={`cx-sla-tab-${activeView}`}>
          <div className="cx-sla-rule-row">
            <h3 className="tl-detail-title">{view.label}</h3>
            <span className="analytics-section-caption">{view.rules}</span>
          </div>

          <div className="cx-sla-scope-row">
            <span className="cx-sla-scope-label">Show</span>
            <div className="cx-sla-scope-control" role="group" aria-label="Filter by charge direction">
              {[{ key: "all", label: "All" }, ...DIRECTIONS.map(({ key, label }) => ({ key, label }))].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`cx-sla-scope-button${scope === item.key ? " cx-sla-scope-button-active" : ""}`}
                  aria-pressed={scope === item.key}
                  onClick={() => {
                    setSelectedStatus("all");
                    setScope(item.key);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <WarningBanner warning={warning} />
          {result.error && <div className="overview-card-error">Failed to load CX Timeliness: {result.error}</div>}
          {result.loading && <div className="overview-card-loading">Loading CX SLA data…</div>}

          {!result.loading && !result.error && (
            <>
              <div className="stat-tile-row cx-sla-stat-row">
                <div
                  className={`cx-sla-kpi-button${selectedStatus === "within_sla" ? " cx-sla-kpi-button-active" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedStatus === "within_sla"}
                  aria-disabled={result.loadingMore}
                  onClick={() => toggleStatus("within_sla")}
                  onKeyDown={handleStatusKeyDown("within_sla")}
                  title="Show charges within SLA"
                >
                  <StatTile
                    label="Within SLA"
                    value={formatCount(counts.within)}
                    subValue={`${knownCount ? ((counts.within / knownCount) * 100).toFixed(1) : "0.0"}% of ${formatCount(knownCount)} known outcomes${scope === "all" ? ` · ${breakdown("within_sla")}` : ""}`}
                    status="good"
                  />
                </div>
                <div
                  className={`cx-sla-kpi-button${selectedStatus === "breached" ? " cx-sla-kpi-button-active" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedStatus === "breached"}
                  aria-disabled={result.loadingMore}
                  onClick={() => toggleStatus("breached")}
                  onKeyDown={handleStatusKeyDown("breached")}
                  title="Show charges not within SLA"
                >
                  <StatTile
                    label="Not Within SLA"
                    value={formatCount(counts.breached)}
                    subValue={`${knownCount ? ((counts.breached / knownCount) * 100).toFixed(1) : "0.0"}% of ${formatCount(knownCount)} known outcomes${scope === "all" ? ` · ${breakdown("breached")}` : ""}`}
                    status="critical"
                  />
                </div>
                <div
                  className={`cx-sla-kpi-button${selectedStatus === "unknown" ? " cx-sla-kpi-button-active" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedStatus === "unknown"}
                  aria-disabled={result.loadingMore}
                  onClick={() => toggleStatus("unknown")}
                  onKeyDown={handleStatusKeyDown("unknown")}
                  title="Show charges with missing SLA date data"
                >
                  <StatTile
                    label="Unknown"
                    value={formatCount(counts.unknown)}
                    subValue={`Required date data missing${scope === "all" ? ` · ${breakdown("unknown")}` : ""}`}
                    status="warning"
                  />
                </div>
              </div>

              <p className="cx-sla-rate-note">Within-SLA and Not-Within-SLA percentages exclude records with unknown dates.</p>

              <details className="analytics-collapsible cx-sla-table-wrap">
                <summary>Charge records — {formatCount(selectedTotal)} rows</summary>
                <div className="cx-sla-table-heading">
                  <span className="analytics-section-caption">
                    {`Showing ${formatCount(tableData.length ? (tablePage - 1) * PAGE_SIZE + 1 : 0)}–${formatCount(Math.min(tablePage * PAGE_SIZE, tableData.length))} of ${formatCount(selectedTotal)}`}
                  </span>
                </div>
                {visibleTableData.length > 0 ? (
                  <div className="cx-sla-table-scroll">
                    <McTable data={visibleTableData} datakey="_id" zebrastripes truncate fit="small" columns={tableColumns} />
                  </div>
                ) : (
                  <p className="cx-sla-empty">
                    {result.loadingMore ? "Finding matching records…" : "No charge records match the selected filters."}
                  </p>
                )}
                {selectedTotal > 0 && (
                  <div className="cx-sla-pagination" aria-label="Charge records pagination">
                    <button
                      type="button"
                      className="cx-sla-page-button"
                      onClick={() => handlePageChange(tablePage - 1)}
                      disabled={tablePage <= 1 || result.loadingMore}
                    >
                      Previous
                    </button>
                    <span className="cx-sla-page-indicator">Page {tablePage} of {pageCount}</span>
                    <button
                      type="button"
                      className="cx-sla-page-button"
                      onClick={() => handlePageChange(tablePage + 1)}
                      disabled={tablePage >= pageCount || result.loadingMore}
                    >
                      {result.loadingMore ? "Loading…" : "Next"}
                    </button>
                  </div>
                )}
              </details>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CXTimelinessSection;
